'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { track } from '@/lib/analytics';
import type { AuthTrigger } from '@/lib/analytics/events';
import { useAccountModal } from '@/contexts/account-modal-context';
import { useAuth } from '@/contexts/auth-context';
import { usePlayheadDistance } from '@/contexts/studio-context';
import { useShortViewport } from '@/hooks/use-short-viewport';
import type { JourneySnapshot } from '@/lib/learning-journey/journey';
import { hasPromptBeenShown } from '@/components/ui/organisms/practice-feedback-prompt/prompt-storage';
// Le délai appartient à la frontière d'étape, pas à la capsule de retour : il
// est la somme du roll qui défile, du fondu du shell et de son ressort de mise
// en page. Le voisin le documente en détail ; le recopier ici le ferait
// diverger le jour où ce ballet change.
import { PROMPT_DELAY_MS } from '@/components/ui/organisms/practice-feedback-prompt/feedback-prompt-palette';
import {
  canInviteToAccount,
  recordAccountInvitation,
} from '@/components/ui/organisms/account-prompt/account-prompt-storage';
import { isAccountPromptDue } from '@/components/ui/organisms/account-prompt/account-prompt-rules';

/**
 * Décide *quand* proposer un compte, et rien d'autre.
 *
 * Le pourquoi du moment, des seuils et de l'arbitrage avec la capsule de
 * retour est dans `account-prompt-rules` ; la politique de répétition dans
 * `account-prompt-storage`. Ce hook n'est que la plomberie entre les deux et
 * React : détecter la frontière, geler ce qui doit l'être, poser le minuteur.
 */

export type UseAccountPromptOptions = {
  readonly snapshot: JourneySnapshot | null;
};

export type UseAccountPromptResult = {
  readonly visible: boolean;
  /** Ouvre la modale de compte et referme la capsule. */
  readonly accept: () => void;
  /** Referme sans rien ouvrir. La porte reste entrouverte pour plus tard. */
  readonly dismiss: () => void;
};

const TRIGGER: AuthTrigger = 'phrase_validated';

export function useAccountPrompt(opts: UseAccountPromptOptions): UseAccountPromptResult {
  const { snapshot } = opts;

  const { isAuthenticated, isLoading } = useAuth();
  const { openAccountModal } = useAccountModal();
  const shortViewport = useShortViewport();
  const playheadDistance = usePlayheadDistance();

  const [visible, setVisible] = useState(false);

  /**
   * Zéro jusqu'au montage : `Date.now()` dans l'initialiseur de `useRef`
   * s'exécute pendant le rendu, ce qui est impur — React peut rejouer un rendu
   * et l'horodatage bougerait sous les pieds de la mesure.
   */
  const sessionStartedAtRef = useRef(0);
  /**
   * L'état de la capsule de retour tel qu'il était à l'ouverture de la
   * session, jamais relu ensuite. C'est ce gel qui empêche les deux
   * invitations de se suivre : si le retour tombe pendant cette session, cette
   * valeur reste `false` et le compte se tait jusqu'au prochain chargement.
   */
  const feedbackSettledRef = useRef(false);
  const prevPhraseIndexRef = useRef<number | null>(null);
  /** Une invitation par session au plus, quel qu'ait été son sort. */
  const spentRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Lu par le nettoyage, qui ne doit consigner un abandon que si la capsule était à l'écran. */
  const visibleRef = useRef(false);
  /**
   * L'invitation a déjà reçu son sort — acceptée ou refusée.
   *
   * Sans ce drapeau, quitter le parcours juste après un clic émettrait un
   * abandon par-dessus : `setVisible(false)` ne se répercute sur `visibleRef`
   * qu'au rendu suivant, et un démontage dans la même passe lirait encore
   * `true`.
   */
  const settledRef = useRef(false);

  // Déclaré avant l'armement pour que l'horodatage et la lecture du voisin
  // soient posés quand celui-ci s'exécute : les effets partent dans leur ordre
  // de déclaration.
  useEffect(() => {
    sessionStartedAtRef.current = Date.now();
    feedbackSettledRef.current = hasPromptBeenShown();
  }, []);

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  // ── Armement ────────────────────────────────────────────────────
  useEffect(() => {
    if (snapshot === null || spentRef.current || timerRef.current !== null) {
      return;
    }

    const idx = snapshot.currentPhraseIndex;
    const prev = prevPhraseIndexRef.current;
    prevPhraseIndexRef.current = idx;

    // Seule une *transition* est une frontière d'étape. Le premier rendu n'en
    // est pas une : personne n'a encore rien réussi.
    if (prev === null || prev === idx) {
      return;
    }

    if (sessionStartedAtRef.current === 0) {
      return;
    }

    const due = isAccountPromptDue({
      // `isLoading` compte autant que le reste : tant que le jeton n'est pas
      // validé, quelqu'un de connecté se lit comme anonyme, et proposer un
      // compte à qui en a déjà un est la seule erreur vraiment embarrassante
      // que cette capsule puisse commettre.
      anonymous: !isLoading && !isAuthenticated,
      phrasesValidated: snapshot.phraseProgress.filter(
        (progress) => progress.etatMax === 'DONE',
      ).length,
      sessionDurationMs: Date.now() - sessionStartedAtRef.current,
      feedbackPromptSettled: feedbackSettledRef.current,
      shortViewport,
      playheadDistancePx: playheadDistance,
    });

    if (!due) {
      return;
    }

    // Le plafond et l'écart d'une semaine se lisent en dernier : ce sont les
    // seules gardes qui coûtent un accès au stockage, et les précédentes
    // écartent déjà l'immense majorité des frontières.
    if (!canInviteToAccount(Date.now())) {
      // Plus rien à dépenser sur ce navigateur, ou trop tôt. On marque la
      // session consommée pour ne plus retester à chaque frontière.
      spentRef.current = true;

      return;
    }

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      spentRef.current = true;

      // L'écriture a lieu ici, à l'affichage, et non à la réponse : le cas le
      // plus probable est l'onglet fermé pendant que la capsule est à l'écran,
      // et il doit compter comme une occasion dépensée.
      recordAccountInvitation(Date.now());
      setVisible(true);

      // C'est la seule exposition comptée pour ce déclencheur : l'écran de
      // compte, lui, émettra `auth_prompt_opened` s'il s'ouvre, parce que son
      // ouverture est alors un clic et non une proposition de plus.
      track({ name: 'auth_prompt_shown', properties: { trigger: TRIGGER } });
    }, PROMPT_DELAY_MS);
  }, [snapshot, shortViewport, playheadDistance, isAuthenticated, isLoading]);

  // ── Nettoyage ───────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (visibleRef.current && !settledRef.current) {
        // Le parcours a été quitté sans que la capsule soit ni acceptée ni
        // refermée. Ne rien faire est une réponse, et elle se compte comme un
        // refus : l'occasion est dépensée de la même façon.
        track({
          name: 'auth_prompt_dismissed',
          properties: { trigger: TRIGGER, hadEmail: false },
        });
      }
    };
  }, []);

  /**
   * L'ouverture de la modale est faite ici et non par la capsule : le trigger
   * décrit le moment, c'est-à-dire ce que ce hook est seul à connaître. La
   * capsule ne sait que dire oui ou non.
   */
  const accept = useCallback(() => {
    if (settledRef.current) {
      return;
    }

    settledRef.current = true;
    setVisible(false);
    openAccountModal(TRIGGER);
  }, [openAccountModal]);

  const dismiss = useCallback(() => {
    if (settledRef.current) {
      return;
    }

    settledRef.current = true;
    setVisible(false);

    // `hadEmail` est structurellement faux : la capsule n'a pas de champ, elle
    // n'ouvre qu'une porte. La propriété appartient au formulaire, qu'on
    // réutilise plutôt que de dupliquer l'événement pour un seul champ mort.
    track({
      name: 'auth_prompt_dismissed',
      properties: { trigger: TRIGGER, hadEmail: false },
    });
  }, []);

  return { visible, accept, dismiss };
}
